import { FunctionComponent, useContext, useEffect, useState } from 'react'
import { Box, Checkbox, Chip, FormControlLabel, Link, Stack, Typography } from '@mui/material'
import { DialogWrapper } from '../display-elements-components/dialog-wrapper'
import { UserNotificationsContext } from '../../../context/user-notifications/user-notifications'
import { IComplianceCalendarEntry } from '../../../shared/services/regulation-applicability/regulation-applicability.model'
import { downloadComplianceCalendarIcs } from '../../../shared/utils/compliance-calendar-ics'

// Which calendar entries this browser has already exported, by entry key. A per-browser
// convenience only: it pre-unticks entries the person has added before, and nothing depends on it.
const STORAGE_KEY = 'complianceCalendarExportedKeys'

const readExportedKeys = (): Set<string> => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    const parsed = raw ? JSON.parse(raw) : []
    return new Set(Array.isArray(parsed) ? parsed.filter((k) => typeof k === 'string') : [])
  } catch {
    return new Set()
  }
}

const rememberExportedKeys = (keys: string[]) => {
  try {
    const merged = new Set([...readExportedKeys(), ...keys])
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify([...merged]))
  } catch {
    // Storage can be unavailable (private window, blocked site data); the download still happened.
  }
}

const formatCalendarDate = (isoDate: string): string =>
  new Date(`${isoDate.slice(0, 10)}T00:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })

/**
 * Pick which deadlines go into the .ics file. Entries exported from this browser before start
 * unticked and are marked, so someone who added last month's dates does not add them twice.
 */
export const ComplianceCalendarDialog: FunctionComponent<{
  open: boolean,
  onClose: () => void,
  entries: IComplianceCalendarEntry[],
  chipLabel: (entry: IComplianceCalendarEntry) => string,
}> = ({ open, onClose, entries, chipLabel }) => {
  const userNotificationsContext = useContext(UserNotificationsContext)
  const [exportedKeys, setExportedKeys] = useState<Set<string>>(new Set())
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set())

  useEffect(() => {
    if (!open) return
    const exported = readExportedKeys()
    setExportedKeys(exported)
    setSelectedKeys(new Set(entries.filter((entry) => !exported.has(entry.key)).map((entry) => entry.key)))
  }, [open, entries])

  const toggle = (key: string) => {
    setSelectedKeys((previous) => {
      const next = new Set(previous)
      if (next.has(key)) {
        next.delete(key)
      } else {
        next.add(key)
      }
      return next
    })
  }

  const selectedEntries = entries.filter((entry) => selectedKeys.has(entry.key))

  const downloadHandler = () => {
    if (!selectedEntries.length) return
    downloadComplianceCalendarIcs(selectedEntries)
    rememberExportedKeys(selectedEntries.map((entry) => entry.key))
    userNotificationsContext.openSnackbar({
      open: true,
      message: `Calendar file created with ${selectedEntries.length} ${selectedEntries.length === 1 ? 'event' : 'events'}`,
    })
    onClose()
  }

  return (
    <DialogWrapper
      open={open}
      onClose={onClose}
      title='Add to your calendar'
      cancelHandler={onClose}
      submitHandler={downloadHandler}
      isSubmitting={false}
      disableSaveButton={selectedEntries.length === 0}
      submitButtonText={selectedEntries.length ? `Download ${selectedEntries.length} ${selectedEntries.length === 1 ? 'event' : 'events'}` : 'Download'}
    >
      <Stack spacing={1.5}>
        <Typography variant='body2' sx={{ color: 'text.secondary' }}>
          Choose the deadlines to include in the calendar file. Dates you have added from this browser before start unticked.
          Each event keeps the same identifier, so adding one again updates it in most calendars rather than creating a duplicate.
        </Typography>
        <Stack direction='row' spacing={2}>
          <Link component='button' type='button' onClick={() => setSelectedKeys(new Set(entries.map((entry) => entry.key)))} sx={{ fontSize: '0.875rem' }}>
            Select all
          </Link>
          <Link component='button' type='button' onClick={() => setSelectedKeys(new Set())} sx={{ fontSize: '0.875rem' }}>
            Clear
          </Link>
        </Stack>
        <Stack>
          {entries.map((entry) => (
            <Box key={entry.key} sx={{ py: 0.5, borderBottomWidth: '1px', borderBottomStyle: 'solid', borderBottomColor: 'lightGrey.main' }}>
              <FormControlLabel
                sx={{ alignItems: 'flex-start', m: 0, width: '100%' }}
                control={<Checkbox size='small' checked={selectedKeys.has(entry.key)} onChange={() => toggle(entry.key)} sx={{ pt: 0.25 }} />}
                label={
                  <Stack spacing={0.25}>
                    <Typography variant='body2' sx={{ color: 'text.primary' }}>{entry.label}</Typography>
                    <Stack direction='row' spacing={1} alignItems='center' flexWrap='wrap' useFlexGap>
                      <Typography variant='caption' sx={{ color: 'text.secondary' }}>{formatCalendarDate(entry.date)}</Typography>
                      <Chip size='small' variant='outlined' sx={{ color: 'text.secondary' }} label={chipLabel(entry)} />
                      {exportedKeys.has(entry.key) && <Chip size='small' variant='outlined' color='info' label='Added before' />}
                    </Stack>
                  </Stack>
                }
              />
            </Box>
          ))}
        </Stack>
      </Stack>
    </DialogWrapper>
  )
}
